import React from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Pagination,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';

interface PaginationFooterProps {
  totalVehicles: number;
  currentPage: number;
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (event: SelectChangeEvent<number>, child: React.ReactNode) => void;
}

const PaginationFooter: React.FC<PaginationFooterProps> = ({
  totalVehicles,
  currentPage,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
}) => {
  const theme = useTheme();
  const totalPages = Math.ceil(totalVehicles / rowsPerPage);

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 8,
        left: 8,
        right: 8,
        height: '40px',
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        zIndex: 10,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2,
        borderRadius: 5,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 0 10px rgba(0,0,0,0.6)'
          : '0 0 6px rgba(0,0,0,0.1)',
      }}
    >
      {/* Rows Per Page */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ color: theme.palette.text.primary, mr: 1 }}>
          Rows:
        </Typography>
        <Select
          value={rowsPerPage}
          onChange={onRowsPerPageChange}
          size="small"
          sx={{
            color: theme.palette.text.primary,
            height: '30px',
            border: `1px solid ${theme.palette.divider}`,
            '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
          }}
        >
          {[10, 25, 50, 100].map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Total Vehicles */}
      <Typography sx={{ color: theme.palette.text.primary }}>
        Total Vehicles: {totalVehicles.toLocaleString()}
      </Typography>

      {/* Pagination */}
      <Pagination
        count={totalPages}
        page={currentPage}
        onChange={onPageChange}
        color="primary"
        sx={{
          '& .Mui-selected': {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.getContrastText(theme.palette.error.main),
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
          },
        }}
      />
    </Box>
  );
};

export default PaginationFooter;
